import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { move, fileToUrl } from "../../lib/adminHelpers";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { UploadField } from "./UploadField";
import { RowControls, DraftBadge } from "./RowControls";
import { AudioPreview } from "./AudioPreview";

const empty = { title: "", description: "", project_type: "", client: "", cover_url: "", video_url: "", before_path: "", after_path: "", tracks: [] };
const newTrack = () => ({ _uid: crypto.randomUUID(), title: "", before_path: "", after_path: "" });

const Meta = ({ f, set }) => (
  <>
    <div><Label className="text-xs text-[#A1A1AA]">Title</Label>
      <Input value={f.title} onChange={(e) => set({ ...f, title: e.target.value })} data-testid="ba-title" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
    <div><Label className="text-xs text-[#A1A1AA]">Description</Label>
      <Input value={f.description} onChange={(e) => set({ ...f, description: e.target.value })} data-testid="ba-desc" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
    <div className="grid grid-cols-2 gap-2">
      <div><Label className="text-xs text-[#A1A1AA]">Project type</Label>
        <Input value={f.project_type} onChange={(e) => set({ ...f, project_type: e.target.value })} placeholder="Music EP" className="bg-[#0B0C10] border-white/10 mt-1" /></div>
      <div><Label className="text-xs text-[#A1A1AA]">Client / Artist</Label>
        <Input value={f.client} onChange={(e) => set({ ...f, client: e.target.value })} className="bg-[#0B0C10] border-white/10 mt-1" /></div>
    </div>
    <UploadField label="Album art" accept="image/*" testid="ba-cover-upload" current={f.cover_url ? "Art set" : ""} onUploaded={(p) => set({ ...f, cover_url: fileToUrl(p) })} />
    <div><Label className="text-xs text-[#A1A1AA]">Film / video URL (optional)</Label>
      <Input value={f.video_url} onChange={(e) => set({ ...f, video_url: e.target.value })} placeholder="https://youtube.com/..." className="bg-[#0B0C10] border-white/10 mt-1" /></div>
  </>
);

const Tracks = ({ f, set, prefix }) => {
  const upd = (i, patch) => set({ ...f, tracks: f.tracks.map((t, x) => (x === i ? { ...t, ...patch } : t)) });
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-[#A1A1AA]">Tracks ({f.tracks.length})</Label>
        <Button type="button" onClick={() => set({ ...f, tracks: [...f.tracks, newTrack()] })} size="sm" variant="outline" data-testid={`${prefix}-add-track`} className="h-7 border-white/15 bg-transparent text-white hover:bg-white/5 rounded-sm text-xs"><Plus size={13} className="mr-1" /> Add track</Button>
      </div>
      {f.tracks.map((t, i) => (
        <div key={t._uid || i} className="rounded border border-white/10 p-3 space-y-2 relative">
          <button onClick={() => set({ ...f, tracks: f.tracks.filter((_, x) => x !== i) })} data-testid={`${prefix}-rm-track-${i}`} className="absolute top-2 right-2 text-[#E04F5F] hover:opacity-70"><X size={14} /></button>
          <Input value={t.title} onChange={(e) => upd(i, { title: e.target.value })} placeholder={`Track ${i + 1} title`} data-testid={`${prefix}-track-title-${i}`} className="bg-[#0B0C10] border-white/10 h-8 text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <UploadField label="" testid={`${prefix}-track-raw-${i}`} current={t.before_path ? "Raw set" : ""} onUploaded={(p) => upd(i, { before_path: p })} />
            <UploadField label="" testid={`${prefix}-track-mastered-${i}`} current={t.after_path ? "Mastered set" : ""} onUploaded={(p) => upd(i, { after_path: p })} />
          </div>
        </div>
      ))}
      {f.tracks.length === 0 && <p className="text-xs text-[#6b6b73]">Add tracks — each needs a title, a raw upload and a mastered upload.</p>}
    </div>
  );
};

export const MasteringManager = ({ bump, onChange }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ ...empty });
  const [mode, setMode] = useState("ep");
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/before-after").then((r) => setItems(r.data)).catch(() => {});
  const done = () => { load(); onChange && onChange(); };
  useEffect(() => { load(); }, [bump]);

  const buildPayload = (f, m) => {
    if (m === "ep") {
      const valid = f.tracks.filter((t) => t.title && t.before_path && t.after_path);
      return { ...f, tracks: valid, before_path: "", after_path: "" };
    }
    return { ...f, tracks: [] };
  };
  const add = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    const p = buildPayload(form, mode);
    if (mode === "ep" && p.tracks.length === 0) { toast.error("Add at least one complete track"); return; }
    if (mode === "single" && (!form.before_path || !form.after_path)) { toast.error("Both raw + mastered audio required"); return; }
    await api.post("/admin/before-after", { ...p, status: "draft", order: items.length });
    toast.success("Added as Draft — click Go live to publish"); setForm({ ...empty }); done();
  };
  const save = async () => {
    if (!editing.title) { toast.error("Title required"); return; }
    const isEp = (editing.tracks || []).length > 0;
    const p = buildPayload(editing, isEp ? "ep" : "single");
    await api.put(`/admin/before-after/${editing.id}`, p);
    toast.success("Saved"); setEditing(null); done();
  };
  const publish = async (it) => { await api.put(`/admin/before-after/${it.id}`, { ...it, status: "published" }); toast.success("Published"); done(); };
  const del = async (id) => { await api.delete(`/admin/before-after/${id}`); done(); };
  const toggleHide = async (it) => { await api.put(`/admin/before-after/${it.id}`, { ...it, hidden: !it.hidden }); done(); };
  const reorder = async (i, dir) => { const next = move(items, i, dir); if (!next) return; await api.post("/admin/before-after/reorder", { ids: next.map((x) => x.id) }); load(); };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="rounded-lg border border-white/10 bg-[#12141A] p-6 h-fit">
        <h3 className="font-head text-lg mb-4 flex items-center gap-2"><Plus size={18} className="text-[#C8A97E]" /> Add Comparison</h3>
        <div className="inline-flex p-1 rounded-md bg-[#0B0C10] border border-white/10 mb-4">
          <button onClick={() => setMode("ep")} data-testid="ba-mode-ep" className={`px-4 py-1.5 text-xs font-semibold rounded ${mode === "ep" ? "bg-[#C8A97E] text-[#0B0C10]" : "text-[#A1A1AA]"}`}>EP / Multi-track</button>
          <button onClick={() => setMode("single")} data-testid="ba-mode-single" className={`px-4 py-1.5 text-xs font-semibold rounded ${mode === "single" ? "bg-[#C8A97E] text-[#0B0C10]" : "text-[#A1A1AA]"}`}>Single track</button>
        </div>
        <div className="space-y-3">
          <Meta f={form} set={setForm} />
          {mode === "single" ? (
            <>
              <UploadField label="Raw audio" testid="ba-before" current={form.before_path ? "Raw set" : ""} onUploaded={(p) => setForm({ ...form, before_path: p })} />
              <UploadField label="Mastered audio" testid="ba-after" current={form.after_path ? "Mastered set" : ""} onUploaded={(p) => setForm({ ...form, after_path: p })} />
            </>
          ) : <Tracks f={form} set={setForm} prefix="ba" />}
          <Button onClick={add} data-testid="ba-add" className="w-full bg-[#C8A97E] text-[#0B0C10] hover:bg-[#B3956D] font-semibold rounded-sm">Add Comparison</Button>
        </div>
      </div>

      <div className="space-y-2">
        {items.length === 0 && <p className="text-[#A1A1AA]">No comparisons yet.</p>}
        {items.map((b, i) => (
          <div key={b.id} data-testid={`ba-row-${b.id}`} className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${b.hidden ? "border-white/5 bg-[#0e0f14] opacity-60" : "border-white/10 bg-[#12141A]"}`}>
            <div className="min-w-0 flex items-center gap-3">
              {b.cover_url && <img src={b.cover_url} alt="" className="h-10 w-10 rounded object-cover shrink-0" />}
              <div className="min-w-0">
                <div className="font-medium truncate flex items-center gap-2">{b.title}<DraftBadge show={b.status === "draft"} />{b.hidden && <span className="text-[10px] uppercase text-[#E0A44F]">hidden</span>}</div>
                <div className="text-xs text-[#A1A1AA] truncate">{b.tracks?.length ? `${b.tracks.length} tracks` : "Single"}</div>
              </div>
            </div>
            <RowControls testid={`ba-${b.id}`} hidden={b.hidden} isFirst={i === 0} isLast={i === items.length - 1}
              isDraft={b.status === "draft"} onPublish={() => publish(b)}
              onUp={() => reorder(i, -1)} onDown={() => reorder(i, 1)}
              onToggleHide={() => toggleHide(b)} onEdit={() => setEditing({ ...empty, ...b, tracks: (b.tracks || []).map((t) => ({ _uid: crypto.randomUUID(), ...t })) })} onDelete={() => del(b.id)}
              extra={<AudioPreview src={b.tracks?.length ? b.tracks[0].after_path : b.after_path} testid={`ba-preview-${b.id}`} />} />
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#12141A] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="ba-edit-dialog">
          <DialogHeader><DialogTitle className="font-head">Edit Comparison</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Meta f={editing} set={setEditing} />
              {(editing.tracks || []).length > 0 || !editing.before_path ? (
                <Tracks f={editing} set={setEditing} prefix="ba-edit" />
              ) : (
                <>
                  <UploadField label="Raw audio" testid="ba-edit-before" current={editing.before_path ? "Raw set" : ""} onUploaded={(p) => setEditing({ ...editing, before_path: p })} />
                  <UploadField label="Mastered audio" testid="ba-edit-after" current={editing.after_path ? "Mastered set" : ""} onUploaded={(p) => setEditing({ ...editing, after_path: p })} />
                </>
              )}
              <Button onClick={save} data-testid="ba-save" className="w-full bg-[#C8A97E] text-[#0B0C10] hover:bg-[#B3956D] font-semibold rounded-sm">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
